import { Capacitor } from "@capacitor/core";

interface BlaxWebSocket {
  socket_id?: string;
  readyState?: 0|1|2|3;
  send: (data: any) => void;
  addEventListener: (event: string, callback: (data: any) => void) => any;
  removeEventListener: (event: string, callback: (data: any) => void) => any;
}

interface BlaxWebSocketClass{
  socket: BlaxWebSocket | null;
  channels: any[];
  is_opened: Ref<boolean>;
  is_setup: Ref<boolean>;
  is_connecting_socket: Ref<boolean>;
  is_after_lost_connection: Ref<boolean>;
  heartbeat: any;
  last_reconnect_try: number;
  send_queue: any[];
  connect: (force_reset?: boolean) => Promise<BlaxWebSocket>;
  channel: (channel_name: string) => Promise<WebsocketChannel|undefined>;
  send<T>(event: string, data?: object, channel_name?: string|null, progress?: (data: any) => void): Promise<any|T>;
  unsubscribe: (channel_name: string|null|undefined) => Promise<boolean>;
  listenOnceFor: (event: string, channel_name: string) => Promise<any>;
  listenWhileMounted<T>(event: string, channel_name: string|null|undefined, callback: (data: T) => void): void;
}


class WebsocketChannel extends EventTarget{
  name: string
  is_establishing: boolean = false
  is_established: boolean = false
  authresponse: any = null

  constructor(
    channel_name: string
  ) {
    console.log('[wc] %cCreating new channel ' + channel_name ,'color:#aaFFff')
    super()
    this.name = channel_name
    ws.channels.push(this)
  }

  async establish() {
    if(this.is_established && ws.is_setup.value){
      return new Promise((resolve, _) => {
        resolve(this)
      })
    }

    if (this.is_establishing){
      console.log('[wc] Awaiting Establishing', this.name)
      await this.awaitEstablishing()
    }

    console.log('[wc] %cEstablishing '+ this.name,'color:#ff00ff')
    this.is_establishing = true

    // await ws.is_opened.value to avoid race condition
    await (new Promise((resolve, _) => {
      const inter = setInterval(() => {
        if(ws.is_opened.value){
          console.log('[wc] %cAwaited socket opened','color:lightgreen')
          clearInterval(inter)
          resolve(true)
        }else{
          console.log('[wc] %cAwaiting socket open','color:lightgreen')

          if (Date.now() - ws.last_reconnect_try > 4000){
            ws.connect(true)
          }
        }
      }
      , 100)
    }))

    await (new Promise((resolve, _) => {
      const inter = setInterval(() => {
        if(ws.socket?.socket_id){
          console.log('[wc] %cAwaited socket id','color:lightgreen')
          clearInterval(inter)
          resolve(true)
        }else{
          console.log('[wc] %cAwaiting socket id','color:lightgreen')
        }
      }, 100)
    }))

    const res = (this.authresponse) ? this.authresponse : await new Promise((resolve, reject) => {
      api.post('broadcasting/auth',{
        socket_id: ws?.socket?.socket_id,
        channel_name: this?.name
      },{
        'Content-Type': 'application/x-www-form-urlencoded',
      }).then((r) =>  {
        this.authresponse = r
        resolve(r)
        return r;
      }).catch((error) => {
        this.is_establishing = false
        this.is_established = false

        reject(error)
      })
    })

    return await this.send('pusher.subscribe', {
      channel: this.name,
      auth: res?.data?.auth,
      authtoken: user?.value?.token ?? localStorage.getItem('bearerToken')
    }).then((_) => {
      console.log('[ws] %cChannel established' + this.name,'color:pink')
      this.is_established = true
      ws.is_setup.value = true
      return this
    })
  }

  async awaitEstablishing() {
    return new Promise((resolve, _) => {
      const inter = setInterval(() => {
        if(this.is_established){
          clearInterval(inter)
          resolve(this)
        }
      }, 100)
    })
  }

  async send(event: string, data: any) {
    if(!this.is_establishing && !this.is_established && event != 'pusher.subscribe'){
      console.log('[wc] Awaiting Establishing', this.name)
      await this.establish()
    }

    if (this.is_establishing && !this.is_established && event != 'pusher.subscribe') {
      return new Promise((resolve, _) => {
        const inter = setInterval(() => {
          if(this.is_established){
            clearInterval(inter)
            resolve(ws.send(
              event,
              data,
              null
            ))
          }
        }, 100)
      })
    }

    return ws.send(
      event,
      data,
      this.name
    )
  }

  async unsubscribe() {
    if(this.is_established){
      await ws.send('pusher.unsubscribe', {
        channel: this.name
      }, this.name).catch(() => {})
    }

    // remove from list
    ws.channels = ws.channels.filter((c) => c.name != this.name)

    return true
  }
}

export const ws = new class extends EventTarget{
  default_channel = 'websocket'

  socket: BlaxWebSocket | null = null
  channels: any[] = []
  is_opened = ref<boolean>(false)
  is_setup = ref<boolean>(false)
  is_connecting_socket = ref<boolean>(false)
  is_after_lost_connection = ref<boolean>(false)
  heartbeat: any
  last_reconnect_try: number = 0

  send_queue: any[] = []

  constructor() {
    super()
    console.log('[ws] Constructed')
  }

  async connect(force_reset: boolean = false) {
    if(
      this.socket?.readyState != 3
      && this.socket?.readyState !== null
      && this.socket?.readyState !== undefined
    ){
      return
    }

    if (this.last_reconnect_try && (Date.now() - this.last_reconnect_try) < 3000) {
      return console.log('[ws] %cReconnect too fast, waiting', 'color:#ff0000');
    }

    this.last_reconnect_try = Date.now()

    console.log('[ws] %cWS Connecting', 'color:#ff0000')

    // === Init heartbeat
    if(force_reset || !this.heartbeat){
      if (this.heartbeat){
        clearInterval(this.heartbeat)
      }
      this.heartbeat = setInterval(() => {
        this.send('pusher.ping')
      }, 20000)
    }

    // === Reset channels
    if(force_reset) {
      this.channels = []
    }

    if(this.channels.length == 0){
      console.log('[WS] No channels, adding '+this.default_channel)
      this.channel()
    }

    // === Reset socket
    if(force_reset){
      this.socket = null
    }

    return new Promise((resolve, reject) => {
      if(this.socket){
        if(this.socket?.readyState === 1){
          return resolve(this.socket);
        }
        return false;
      }

      if(this.is_connecting_socket.value){
        return console.log('[ws] %cSocket already connecting','color:#ff0000')
      }

      this.is_connecting_socket.value = true

      console.log('[ws] %cOpening socket','color:#33ff33')
      this.socket = new WebSocket('wss://'+useRuntimeConfig().public.WEBS_URL) as BlaxWebSocket

      this.socket.addEventListener('error', (error) => {
        console.log('Error', error)
        this.is_connecting_socket.value = false
        // clearInterval(this.heartbeat)
        reject(error)
      })

      this.socket.addEventListener('close', (error) => {
        console.log('Close at '+(Math.round(window.performance.now() / 10)/100), error)
        // clearInterval(this.heartbeat)

        // remove all channels
        this.channels = []
        this.is_connecting_socket.value = false
        this.is_opened.value = false
        this.is_setup.value = false
        this.socket = null

        this.is_after_lost_connection.value = true

        if(!Capacitor.isNativePlatform()){
          notify({
            id: 'websocket-connection-state',
            type: 'info',
            text: useT('websocket.connectionlost'),
            timeout: 50000,
          })
        }

        reject(error)
      })

      this.socket.addEventListener('open', () => {
        if(this.is_after_lost_connection.value && !Capacitor.isNativePlatform()){
          notify({
            id: 'websocket-connection-state',
            type: 'success',
            text: useT('websocket.connectionrestored'),
            timeout: 1000,
          })
        }

        console.log('[ws] %cOpened', 'color:pink')
        this.is_opened.value = true

        setTimeout(() => {
          this.workSendQueue()
        },3000);
      })

      const list = this.socket.addEventListener('message', async (message) => {
        message = JSON.parse(message.data)

        if(message?.data && message?.event == "pusher.connection_established"){
          const data = JSON.parse(message.data)

          if(data?.socket_id && this.socket){
            this.socket.socket_id = data.socket_id

            resolve(this.socket)
            this.socket.removeEventListener('message', list)
          }
        }else{
          // if message.data is string
          if(message?.data && typeof message.data === 'string'){
            try{
              message.data = JSON.parse(message.data)
            }catch(e){
              // ignore
            }
          }

          // fire event within class
          this.dispatchEvent(new CustomEvent(message.event,{
            detail: {
              event: message.event,
              data: message.data,
              channel: message.channel
            }
          }))
        }
      })
    })
  }

  async channel(channel_name: string|null = null) : Promise<WebsocketChannel|undefined> {
    // Check if channel is already existing

    if(!channel_name){
      channel_name = this.default_channel
    }

    let channel = this.channels.find((c) => {
      if(c.name == channel_name) return c
    })

    // Use existing one
    if(!channel){
      channel = new WebsocketChannel(channel_name)
    }

    return channel.establish()
  }

  async workSendQueue() {
    if(this.send_queue.length > 0){
      console.log('[ws] Queue has items', this.send_queue.length)
      const queue = this.send_queue
      this.send_queue = []

      queue.forEach(async (payload) => {
        console.log('[ws] Queue real send %c' + payload.event, 'color:lightgreen')
        await this.channel(payload.channel)
        this.socket?.send(JSON.stringify(payload))
      })
    }else{
      console.log('[ws] Queue is empty')
    }
  }

  async send<T>(
    event: string,
    data: object = {},
    channel_name: string|null,
    progress?: (data: any) => void
  ) : Promise<any|T> {

    if(!channel_name){
      channel_name = this.default_channel
    }

    if(!this.socket){
      console.log('[ws] %cSocket not opened','color:#ff0000')
      await this.connect()
    }

    const unique = '['+Math.random().toString(36).substring(7)+']'
    let sendingevent = event + unique;

    if (event == 'pusher.subscribe') {
      sendingevent = 'pusher.subscribe'
      channel_name = null
    }

    const payload = {
      event: sendingevent,
      data,
      channel: channel_name,
    }

    if( channel_name && !( event == 'pusher:subscribe' || event == 'pusher.subscribe' ) ){
      await this.channel(channel_name as string)
    }

    (this.is_opened.value && this.socket?.readyState === 1)
      ? this.socket.send(JSON.stringify(payload))
      : this.send_queue.push(payload)

    await this.connect()
    if (!this.socket) throw new Error('Socket not connected')

    return new Promise((resolve, reject) => {
      this.addEventListener(sendingevent+':progress', (m) => this.messageReceivedHandler(m, event, sendingevent, channel_name as string, progress, resolve, reject));
      this.addEventListener(sendingevent+':error', (m) => this.messageReceivedHandler(m, event, sendingevent, channel_name as string, progress, resolve, reject));
      this.addEventListener(sendingevent+':response', (m) => this.messageReceivedHandler(m, event, sendingevent, channel_name as string, progress, resolve, reject));
    })
  }

  messageReceivedHandler(
    message: any,
    event: string,
    sendingevent: string,
    channel_name: string,
    progress: ((data: any) => void) | undefined,
    resolve: (value: unknown) => void,
    reject: (reason?: any) => void
  ) {
    const listenstart = window.performance.now()

    message = message.detail

    // Listen for Success
    if(
      (
        event == 'pusher.subscribe'
        && message?.data?.channel == channel_name
      )
      || message?.event.includes(sendingevent+':response')
    ) {
      this.removeEventListener(sendingevent+':progress', (m) => this.messageReceivedHandler(m, event, sendingevent, channel_name, progress, resolve, reject))
      this.removeEventListener(sendingevent+':error', (m) => this.messageReceivedHandler(m, event, sendingevent, channel_name, progress, resolve, reject))
      this.removeEventListener(sendingevent+':response', (m) => this.messageReceivedHandler(m, event, sendingevent, channel_name, progress, resolve, reject))

      console.log('%c[diff.'+sendingevent+'] at '+(Math.round(window.performance.now() / 10)/100),'color:red', window.performance.now() - listenstart)
      resolve(message.data)
    }

    // Listen for Errors
    if(
      message?.event.includes(sendingevent+':error')
      || message?.event.includes(sendingevent+':timeout')
    ){
      this.removeEventListener(sendingevent+':progress', (m) => this.messageReceivedHandler(m, event, sendingevent, channel_name, progress, resolve, reject))
      this.removeEventListener(sendingevent+':error', (m) => this.messageReceivedHandler(m, event, sendingevent, channel_name, progress, resolve, reject))
      this.removeEventListener(sendingevent+':response', (m) => this.messageReceivedHandler(m, event, sendingevent, channel_name, progress, resolve, reject))

      console.log('%c[diff.'+sendingevent+'] at '+(Math.round(window.performance.now() / 10)/100),'color:red', window.performance.now() - listenstart)
      reject(message.data)
    }

    // Listen for Progress
    if (progress && message?.event.includes(sendingevent+':progress')) {
      progress(message.data)
    }
  }

  listenOnceFor(event: string, channel_name: string|null|undefined, unmount: boolean = true){
    if(!channel_name){
      channel_name = this.default_channel
    }

    return new Promise((resolve) => {
      const handler = (m: any) => {
        const r = m.detail
        if(r.channel == channel_name){
          resolve(r.data)
          this.removeEventListener(event, handler)
        }
      }

      this.addEventListener(event, handler)

      if(unmount){
        onUnmounted(() => {
          this.removeEventListener(event, handler)
        })
      }
    })
  }

  listenWhileMounted<T>(event: string, channel_name: string|null|undefined, callback: (data: T) => void){
    if(!channel_name){
      channel_name = this.default_channel
    }

    const handler = (m: any) => {
      const r = m.detail
      if(r.channel == channel_name){
        callback(r.data)
      }
    }

    this.addEventListener(event, handler)

    onUnmounted(() => {
      this.removeEventListener(event, handler)
    })
  }

  async unsubscribe(channel_name: string|null|undefined){
    if(!channel_name){
      channel_name = this.default_channel
    }

    const channel = this.channels.find((c) => {
      if(c.name == channel_name) return c
    })

    if(channel){
      await channel.unsubscribe()
    }

    // remove from list
    this.channels = this.channels.filter((c) => c.name != channel_name)

    return true
  }
} as BlaxWebSocketClass
